import { useCallback } from "react";
import { ApolloError } from "@apollo/client";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import { PayableData } from "#components/PayableWorkflow/Context/types";
import { useCreatePayableMutation } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { TransformResult } from "./types";

export interface UseCreatePayableHandlerOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export interface CreatePayableResult {
  payableId?: string;
  error?: string;
}

export interface UseCreatePayableHandlerReturn {
  createPayable: (payableData: PayableData) => Promise<CreatePayableResult>;
  loading: boolean;
  error: ApolloError | undefined;
}

/**
 * Hook that handles payable creation with proper error handling and success callbacks.
 * Transforms PayableData to CreatePayableInput format and handles the mutation.
 * Returns a result object with either payableId (on success) or error message (on failure).
 */
export const useCreatePayableHandler = (
  options: UseCreatePayableHandlerOptions = {},
): UseCreatePayableHandlerReturn => {
  const { onSuccess, onError } = options;

  const [createPayableMutation, { loading, error }] = useCreatePayableMutation({
    onCompleted: onSuccess,
    onError: (apolloError) => {
      addError(formatError(apolloError));
      onError?.(apolloError);
    },
    context: clientContext(),
  });

  const transformPayableDataToInput = useCallback(
    (payableData: PayableData): TransformResult => {
      if (!payableData.arenaId) {
        return { error: "Arena ID is required" };
      }
      if (!payableData.entityId) {
        return { error: "Investment entity is required" };
      }
      if (!payableData.payeeId) {
        return { error: "Payee ID is required" };
      }
      // Use paymentAmount for both payable and payment creation
      if (!payableData.paymentAmount || payableData.paymentAmount <= 0) {
        return { error: "Valid amount is required" };
      }

      const supportingDocuments = (payableData.invoiceDocuments || []).map((doc) => ({
        name: doc.name,
        size: doc.size,
        s3ObjectKey: doc.s3ObjectKey,
        contentType: doc.contentType || "application/octet-stream",
      }));

      return {
        input: {
          arenaId: payableData.arenaId,
          entityId: payableData.entityId,
          payeeId: payableData.payeeId,
          amount: {
            amount: Math.round(payableData.paymentAmount * 100),
            currency: "USD",
            precision: 2,
          },
          dueDate: payableData.dueDate ? payableData.dueDate.toISOString().split("T")[0] : null,
          invoiceNumber: payableData.invoiceNumber || null,
          invoiceDate: payableData.invoiceDate
            ? payableData.invoiceDate.toISOString().split("T")[0]
            : null,
          description: payableData.description || null,
          supportingDocuments,
        },
      };
    },
    [],
  );

  const createPayable = useCallback(
    async (payableData: PayableData): Promise<CreatePayableResult> => {
      const transformResult = transformPayableDataToInput(payableData);

      if (transformResult.error !== undefined) {
        return { error: transformResult.error };
      }

      // At this point, transformResult.input is guaranteed to be defined
      const input = transformResult.input;

      try {
        const result = await createPayableMutation({
          variables: { input },
        });

        // Check for GraphQL errors in the response
        if (result.errors?.length) {
          const errMsg = result.errors.map((e) => e.message).join(", ");
          return { error: errMsg };
        }

        // Attempt to extract the created payable ID from the mutation response
        const data = result.data;
        if (data && "createPayable" in data) {
          const payload = data.createPayable;

          // Check for MutationErrorResponse (error returned in payload)
          if (payload && payload.__typename === "MutationErrorResponse") {
            return { error: payload.message ?? "Failed to create payable" };
          }

          if (payload && payload.__typename === "CreatePayableResponse") {
            return { payableId: payload.payable.id };
          }
        }

        return { error: "Failed to create payable" };
      } catch (e) {
        // Apollo errors are reported by the mutation's onError callback above;
        // only report unexpected non-Apollo errors here to avoid double-fire.
        if (!(e instanceof ApolloError)) {
          addError(formatError(e));
        }
        return { error: "Unexpected error while creating payable" };
      }
    },
    [createPayableMutation, transformPayableDataToInput],
  );

  return {
    createPayable,
    loading,
    error,
  };
};
